import type { ShippingDocumentKind } from "encamp-prisma/generated/browser";

export const GENERATED_DOCUMENT_NUMBER_PREFIX = "NO-NUMBER-";

export const NO_NUMBER_ON_BILL_LABEL = "No number on bill";
export const NO_FACILITY_LABEL = "No facility";

/**
 * Type column values by `ShippingDocumentKind`. A column value reads "Other",
 * while a name in a sentence or title reads "Shipping Document" (see `DOCUMENT_KIND_NAMES`).
 * A new kind fails to compile until it has a label.
 */
const DOCUMENT_KIND_LABELS: Record<ShippingDocumentKind, string> = {
  MANIFEST: "Manifest",
  BILL_OF_LADING: "Bill of Lading",
  OTHER: "Other",
};

const LABEL_BY_KIND = new Map<string, string>(Object.entries(DOCUMENT_KIND_LABELS));

/**
 * A document kind named in a sentence ("noun"), at a sentence start ("sentence") or in a title
 * ("title"), plus what its number is called in a sentence ("numberNoun") and on a field ("numberLabel").
 */
export type DocumentKindNames = {
  noun: string;
  sentence: string;
  title: string;
  numberNoun: string;
  numberLabel: string;
};

/** Every wording of a kind's name. A new kind fails to compile until it has one. */
const DOCUMENT_KIND_NAMES = {
  MANIFEST: {
    noun: "manifest",
    sentence: "Manifest",
    title: "Manifest",
    numberNoun: "tracking number",
    numberLabel: "Manifest #",
  },
  BILL_OF_LADING: {
    noun: "bill of lading",
    sentence: "Bill of lading",
    title: "Bill of Lading",
    numberNoun: "document number",
    numberLabel: "Document #",
  },
  OTHER: {
    noun: "shipping document",
    sentence: "Shipping document",
    title: "Shipping Document",
    numberNoun: "document number",
    numberLabel: "Document #",
  },
} as const satisfies Record<ShippingDocumentKind, DocumentKindNames>;

/** Used when the kind is not known. */
const NEUTRAL_DOCUMENT_NAMES = {
  noun: "document",
  sentence: "Document",
  title: "Document",
  numberNoun: "document number",
  numberLabel: "Document #",
} as const satisfies DocumentKindNames;

/** Every wording a document can be given, so a caller can type a prop by the literal names. */
type KnownDocumentNames =
  | (typeof DOCUMENT_KIND_NAMES)[keyof typeof DOCUMENT_KIND_NAMES]
  | typeof NEUTRAL_DOCUMENT_NAMES;

export type DocumentKindSentence = KnownDocumentNames["sentence"];
export type DocumentKindNoun = KnownDocumentNames["noun"];

const NAMES_BY_KIND = new Map<string, KnownDocumentNames>(Object.entries(DOCUMENT_KIND_NAMES));

/** The label for a document kind. A value outside the enum shows as given. */
export function documentKindLabel(kind: string): string {
  return LABEL_BY_KIND.get(kind) ?? kind;
}

/**
 * The kind a number implies when no document row says. Only a bill is given a
 * generated number, so that prefix marks a bill; any other number says nothing.
 */
export function documentKindForNumber(manifestTrackingNumber: string): ShippingDocumentKind | null {
  return isGeneratedDocumentNumber(manifestTrackingNumber) ? "BILL_OF_LADING" : null;
}

/** A bill with no printed number is stored under a generated `NO-NUMBER-` value. */
export function isGeneratedDocumentNumber(value: string): boolean {
  return value.startsWith(GENERATED_DOCUMENT_NUMBER_PREFIX);
}

/** The names for a kind. An explicit kind wins, then a generated number marks a bill, else the neutral "document". */
export function documentKindNames(
  kind?: string | null,
  manifestTrackingNumber?: string | null
): KnownDocumentNames {
  const resolved =
    kind ?? (manifestTrackingNumber ? documentKindForNumber(manifestTrackingNumber) : null);
  return (resolved ? NAMES_BY_KIND.get(resolved) : undefined) ?? NEUTRAL_DOCUMENT_NAMES;
}

type DocumentNumberRow = {
  documentKind?: string | null;
  manifestTrackingNumber?: string | null;
};

/** True for a generated `NO-NUMBER-` value. Only bills get one, so the prefix decides over the row's kind. */
export function isNumberlessBill(row: { manifestTrackingNumber?: string | null }): boolean {
  return !!row.manifestTrackingNumber && isGeneratedDocumentNumber(row.manifestTrackingNumber);
}

/** The number a reader should see: a bill's generated number reads as missing. */
export function manifestNumberLabel(row: DocumentNumberRow): string {
  return isNumberlessBill(row) ? NO_NUMBER_ON_BILL_LABEL : (row.manifestTrackingNumber ?? "");
}

/** The document's kind and number, such as "Manifest #123" or "Bill of Lading (No number on bill)". */
export function documentTitle(row: DocumentNumberRow): string {
  if (isNumberlessBill(row)) {
    return `${DOCUMENT_KIND_NAMES.BILL_OF_LADING.title} (${NO_NUMBER_ON_BILL_LABEL})`;
  }
  const kind = documentKindNames(row.documentKind, row.manifestTrackingNumber).title;
  return row.manifestTrackingNumber ? `${kind} #${row.manifestTrackingNumber}` : kind;
}

/** A bill can have no facility. Anything else reads the name, then `fallback`. */
export function facilityLabel(
  row: { documentKind: string; facilityId?: string | null; facilityName?: string | null },
  fallback: string
): string {
  if (row.documentKind === "BILL_OF_LADING" && !row.facilityId) return NO_FACILITY_LABEL;
  return row.facilityName ?? fallback;
}
